import { memo, useEffect, useState, useRef, useCallback } from "react";
import Camera from "@zenith/assets/icons/Camera";
import Eye from "@zenith/assets/icons/Eye";
import Home from "@zenith/assets/icons/Home";
import Info from "@zenith/assets/icons/Info";
import Smile from "@zenith/assets/icons/Smile";
import SilverTierLarge from "@zenith/assets/icons/SilverTierLarge";
import GoldTierLarge from "@zenith/assets/icons/GoldTierLarge";
import PlatinumTierLarge from "@zenith/assets/icons/PlatinumTierLarge";
import { UsersApiService } from "@zenith/services/user/services/UsersApiService";
import {
  ApiResponseDTODiscardUpgradeResponseDTO,
  ImageRequestDTO,
  SelfieStatusResponseDTO,
  StartUpgradeTierResponseDTO,
  TierApiService,
  TierStepsResponseDTO,
  UpgradeProcessInfoDTO,
} from "@zenith/services/user";
import { ModalComponent, SpinnerComponent } from "@zenith/components";
import { AppNavigation, AppStepper, useSelfieVerificationPolling, TierStepper } from "@zenith/hooks";
import Clock from "@zenith/assets/icons/Clock";
import CloseCircle from "@zenith/assets/icons/CloseCircle";
import { CameraComponent } from "@zenith/components";
import { DASHBOARD, UPGRADE_ACCOUNT } from "@zenith/routes/paths";
import { useSelfiesContext, useUpgradeContext } from "@zenith/context";
import StepStatusComponent from "@zenith/features/Tiers/components/StepStatusComponent/step-status-component/step-status-component";
import {
  SELFIE_JOB_ID,
  TIER_TO_UPGRADE_ITEM,
  TIER_UPGRADE_ERROR,
} from "@zenith/data/constants";
import { updateTierStepStatus } from "@zenith/utils/Helpers";
import CheckCircle from "@zenith/assets/icons/CheckCircle";

export interface PhotoVerificationPageProps {
  step: TierStepsResponseDTO;
}

const PhotoVerificationPage = memo(function PhotoVerificationPage(
  props: PhotoVerificationPageProps
) {
  const { navigateTo } = AppNavigation();
  const { setActiveStep } = AppStepper();
  const { goToNextOpenStep } = TierStepper();
  const { setSelfiesContext } = useSelfiesContext();
  const { upgradeContext, setUpgradeContext, clearUpgradeContext } =
    useUpgradeContext();

  const selfieJobId = sessionStorage.getItem(SELFIE_JOB_ID);
  const selectedTier = upgradeContext.tierType;
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const [secondErrorMessage, setSecondErrorMessage] = useState<
    string | undefined
  >(undefined);
  const [jobId, setJobId] = useState<string | undefined>(
    selfieJobId ? JSON.parse(selfieJobId) : ""
  );
  const [selfieStatus, setSelfieStatus] = useState<string | undefined>(
    props.step.stepStatus
  );
  const [backModal, setBackModal] = useState(false);
  const [open, setOpen] = useState(false);
  const [selfies, setSelfies] = useState<ImageRequestDTO[]>([]);
  const [metadata, setMetadata] = useState<string>("");
  const [userBlocked, setUserBlocked] = useState<boolean>(false);
  const [isPhotoSubmitted, setIsPhotoSubmitted] = useState<boolean>(
    props.step.stepStatus !== TierStepsResponseDTO.stepStatus.NOT_STARTED ||
      !!selfieJobId
  );
  const [verifyingSelfie, setVerifyingSelfie] = useState<boolean>(
    !!selfieJobId
  );
  const [isContinueDisabled, setIsContinueDisabled] = useState(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const selfiesSubmittedRef = useRef<boolean>(false);

  const getSmallTierIcon = (
    tier: StartUpgradeTierResponseDTO.tierType | UpgradeProcessInfoDTO.tierName
  ) => {
    const tierProps = {
      width: "32",
      height: "32",
      fill: "currentColor",
    };

    switch (tier.toUpperCase()) {
      case StartUpgradeTierResponseDTO.tierType.SILVER:
        return <SilverTierLarge {...tierProps} />;
      case StartUpgradeTierResponseDTO.tierType.GOLD:
        return <GoldTierLarge {...tierProps} />;
      case StartUpgradeTierResponseDTO.tierType.PLATINUM:
        return <PlatinumTierLarge {...tierProps} />;
      default:
        return null;
    }
  };

  const getTierColour = (
    tier:
      | StartUpgradeTierResponseDTO.tierType
      | UpgradeProcessInfoDTO.tierName
      | undefined
  ) => {
    switch (tier?.toUpperCase()) {
      case StartUpgradeTierResponseDTO.tierType.SILVER:
        return "#F16613";
      case StartUpgradeTierResponseDTO.tierType.GOLD:
        return "#F0B02A";
      case StartUpgradeTierResponseDTO.tierType.PLATINUM:
        return "#6EBD7F";
      default:
        return "#000000";
    }
  };

  const getErrorMessageForSelfieStatus = (status: string) => {
    if (status === "VERIFIED" || status === "VERIFIED_SUCCESSFULLY")
      return null;

    const statusToMessage: Record<
      string,
      { primary: string; secondary?: string }
    > = {
      NO_FACE_FOUND: {
        primary: "There was no face found.",
        secondary: "Upload a higher quality photo.",
      },
      LOW_IMAGE_QUALITY: {
        primary: "The uploaded selfie quality was too poor.",
        secondary: "Upload a higher quality photo.",
      },
      GENERAL_ERROR_IN_VERIFICATION: {
        primary: "It was not possible to verify your selfie.",
        secondary: "Please try again.",
      },
      ERROR_IN_VERIFICATION: {
        primary: "There was a problem verifying your selfie.",
        secondary: "Please try again with a clearer photo.",
      },
    };

    return (
      statusToMessage[status] || {
        primary: "An error occurred during verification.",
        secondary: "Please try again.",
      }
    );
  };

  useEffect(() => {
    if (!sessionStorage.getItem(TIER_TO_UPGRADE_ITEM)) {
      handleBackButton();
    }
  });

  const handleOpenCamera = useCallback(() => {
    setOpen(true);
  }, []);

  const handleModalState = () => {
    setBackModal(!backModal);
  };

  const handleDiscardUpgrade = () => {
    TierApiService.discardUpgradeTier()
      .then((response: ApiResponseDTODiscardUpgradeResponseDTO) => {
        if ("data" in response && response.data) {
          setJobId("");
          setSelfieStatus(undefined);
          setSelfies([]);
          setActiveStep(0);
          clearUpgradeContext();
          navigateTo(UPGRADE_ACCOUNT);
        }
      })
      .catch((error) => {
        return error;
      });
  };

  const handleContinueButton = () => {
    setSelfiesContext(selfies);
    goToNextOpenStep();
  };

  const handleBackButton = () => {
    navigateTo(UPGRADE_ACCOUNT);
  };

  const handleTryAgain = () => {
    if (verifyingSelfie) setVerifyingSelfie(false);
    reset();
    setJobId("");
    sessionStorage.removeItem(SELFIE_JOB_ID);
    setIsPhotoSubmitted(false);
    setSelfieStatus(undefined);
    setErrorMessage(undefined);
    setSecondErrorMessage(undefined);
    selfiesSubmittedRef.current = false;
  };

  const {
    status: selfieVerificationStatus,
    polling,
    error,
    reset,
    lastPollingAttempt,
  } = useSelfieVerificationPolling(
    !!jobId && verifyingSelfie,
    false,
    jobId || "",
    true
  );

  useEffect(() => {
    if (error) {
      setVerifyingSelfie(false);
      setSelfieStatus("ERROR_IN_VERIFICATION");
      sessionStorage.removeItem(SELFIE_JOB_ID);
      setErrorMessage("There was a problem verifying your selfie");
      setSecondErrorMessage("Please try again with a clearer photo");
    }
  }, [error]);

  useEffect(() => {
    if (!selfieVerificationStatus) return;

    setSelfieStatus(selfieVerificationStatus);

    if (selfieVerificationStatus === "ERROR_IN_VERIFICATION") {
      setVerifyingSelfie(false);
      sessionStorage.removeItem(SELFIE_JOB_ID);
      setErrorMessage("There was a problem with photo verification");
      setSecondErrorMessage("Please try again");
    } else if (
      ["VERIFIED", "VERIFIED_SUCCESSFULLY", "NOT_VERIFIED"].includes(
        selfieVerificationStatus
      )
    ) {
      setTimeout(() => setVerifyingSelfie(false), 500);
    }

    const tierStepsUpdated = updateTierStepStatus(
      upgradeContext,
      TierStepsResponseDTO.stepName.SELFIE_VERIFICATION_STEP,
      selfieVerificationStatus?.mapUpgradeStatus() as TierStepsResponseDTO.stepStatus
    );

    setUpgradeContext({
      ...upgradeContext,
      tierSteps: tierStepsUpdated,
    });
  }, [selfieVerificationStatus, upgradeContext, setUpgradeContext]);
  useEffect(() => {
    if (selfies.length > 0 && !selfiesSubmittedRef.current) {
      selfiesSubmittedRef.current = true;
      setIsContinueDisabled(true);
      setIsUploading(true);

      UsersApiService.postSubmitSelfie({ images: selfies })
        .then((response) => {
          setIsUploading(false);
          setIsContinueDisabled(false);

          if (response.data) {
            const newJobId = response.data.jobId;
            setJobId(newJobId);
            sessionStorage.setItem(SELFIE_JOB_ID, JSON.stringify(newJobId));
            setIsPhotoSubmitted(true);
            setVerifyingSelfie(true);
            setErrorMessage(undefined);
            setSecondErrorMessage(undefined);
          }
        })
        .catch((error) => {
          setIsUploading(false);
          setIsContinueDisabled(false);
          selfiesSubmittedRef.current = false;

          if (error.body?.internalCode === TIER_UPGRADE_ERROR) {
            setUserBlocked(true);
          } else {
            setErrorMessage("Failed to submit your selfie");
            setSecondErrorMessage(
              "Please try again or contact support if the issue persists"
            );
          }
        });
    }
  }, [selfies]);

  const renderInstructions = () => (
    <>
      <ul className="flex flex-col gap-y-8 text-base-lh-17 font-normal text-neutral-600">
        <li className="flex flex-row">
          <Camera className="mt-1" />
          <p className="ml-3 w-full">
            We need access to your camera for face verification and to capture
            your photo
          </p>
        </li>
        <li className="flex flex-row">
          <Smile className="mt-1" />
          <p className="ml-3 w-full">
            Please, face the camera and keep a straight face
          </p>
        </li>

        <li className="flex flex-row">
          <Home className="mt-1" />
          <p className="ml-3 w-full">
            Ensure you are in a well lit environment
          </p>
        </li>
        <li className="flex flex-row">
          <Eye className="mt-1" />
          <p className="ml-3 w-full">
            Remove contacts, glasses and other facial accessories
          </p>
        </li>
      </ul>
      <div className="bg-neutral-info my-12 flex w-full items-start rounded-1.25l p-4">
        <p className="mt-0.5">{<Info />}</p>
        <p className="ml-3 text-sm font-normal text-neutral-900">
          Your photograph is safe and will only be used for identification
          purposes.
        </p>
      </div>
    </>
  );

  const shouldShowErrorUI = () => {
    return (
      [
        "ERROR_IN_VERIFICATION",
        TierStepsResponseDTO.stepStatus.REJECTED,
      ].includes(selfieStatus || "") ||
      !!errorMessage ||
      !!error
    );
  };

  return (
    <>
      <ModalComponent
        body="Are you sure you want to leave?"
        secondLine="All information provided will be lost."
        title="Discard Upgrade"
        classNames={`w-[98%] sm:w-2/3 lg:1/3`}
        showModal={backModal}
        onClickClose={handleModalState}
        doubleOptions={true}
        onLeftButtonClick={handleDiscardUpgrade}
        onRightButtonClick={handleModalState}
        leftButtonText="Discard Upgrade"
        rightButtonText="Return to Upgrade"
      />

      <CameraComponent
        open={open}
        setOpen={setOpen}
        detectFace={true}
        getPhotos={(photos) => {
          if (photos.length > 0 && !verifyingSelfie && !jobId) {
            setIsUploading(true);
            setSelfies(photos);
          } else if (verifyingSelfie || jobId) {
            setOpen(false);
          }
        }}
      />

      <p className="mb-4 flex flex-row items-center text-sm font-normal text-neutral-600">
        You are upgrading to{" "}
        {selectedTier && (
          <span className="ml-2 flex items-center">
            {getSmallTierIcon(selectedTier)}
            <span
              className="ml-2 capitalize"
              style={{ color: getTierColour(selectedTier) }}
            >
              {selectedTier.toLowerCase()}
            </span>
          </span>
        )}
      </p>
      <h1 className="mb-10 text-4.5xl font-medium text-neutral-900">
        Photo Verification
      </h1>

      {shouldShowErrorUI() ? (
        <StepStatusComponent
          status={SelfieStatusResponseDTO.status.ERROR_IN_VERIFICATION}
          title={"Photo Verification Issue"}
          subTitle={"Please review the following:"}
          icon={<CloseCircle stroke={"#DA291C"} transform="scale(1.70)" />}
          handleDiscardButton={handleModalState}
          handleTryAgainButton={handleTryAgain}
          errorTitle="Verification Problem"
          errorMessage={errorMessage || "Verification failed"}
          secondErrorMessage={secondErrorMessage}
        />
      ) : !isPhotoSubmitted && !userBlocked ? (
        <>
          {renderInstructions()}
          <div className="stepper-buttons-container">
            <button
              id="selfie-back-button"
              className="secondary-button"
              onClick={handleModalState}
              type="button"
            >
              Discard
            </button>
            <button
              id="continue-to-photo-verification"
              className="primary-button"
              onClick={handleOpenCamera}
              disabled={isContinueDisabled}
            >
              Start Photo Verification
            </button>
          </div>
        </>
      ) : isUploading ? (
        <div className="mt-20 lg:ml-10 lg:flex">
          <div className="flex flex-col items-center">
            <SpinnerComponent size={64} type={"primary"} showIcon={false} />
            <p className="mt-6 text-base font-normal text-neutral-600">
              Uploading your photograph...
            </p>
            <p className="text-sm font-normal text-neutral-400">
              This may take a moment as we process multiple images.
            </p>
          </div>
        </div>
      ) : (isPhotoSubmitted && !userBlocked) ||
        upgradeContext.isRecoveryFlow ? (
        <div>
          {!verifyingSelfie &&
            selfieStatus === TierStepsResponseDTO.stepStatus.PENDING && (
              <StepStatusComponent
                status={SelfieStatusResponseDTO.status.PENDING_VERIFICATION}
                title={"Waiting For Approval"}
                subTitle={'Press "Continue" to proceed.'}
                icon={<Clock transform="scale(1.70)" />}
                handleDiscardButton={handleModalState}
                handleContinueButton={handleContinueButton}
              />
            )}
          {!verifyingSelfie &&
            selfieStatus === TierStepsResponseDTO.stepStatus.APPROVED && (
              <StepStatusComponent
                status={SelfieStatusResponseDTO.status.VERIFIED_SUCCESSFULLY}
                title={"Photo Successfully Verified"}
                subTitle={'Press "Continue" to proceed.'}
                icon={<CheckCircle transform="scale(1.70)" />}
                handleDiscardButton={handleModalState}
                handleContinueButton={handleContinueButton}
              />
            )}
          {verifyingSelfie && (
            <div className="mt-20 lg:ml-10 lg:flex">
              <div className="flex flex-col items-center">
                <SpinnerComponent size={64} type={"primary"} showIcon={false} />
                <p className="mt-6 text-base font-normal text-neutral-600">
                  Your photograph is being verified.
                </p>
                <p className="text-sm font-normal text-neutral-400">
                  This should not take too long.
                </p>
              </div>
            </div>
          )}
        </div>
      ) : userBlocked ? (
        <div className="ml-0 w-full">
          <div className="flex items-center">
            <CloseCircle fill="#DA291C" width={32} height={32} />
            <h4 className="ml-2 text-xl">Photo Verification Failed</h4>
          </div>
          <p className="ml-10 text-sm">
            There have been too many attempts. For security reasons, please wait
            12 hours before trying again.
          </p>
          <button
            className="primary-button mt-20"
            onClick={() => navigateTo(DASHBOARD)}
          >
            <p>Go to Dashboard</p>
          </button>
        </div>
      ) : null}
    </>
  );
});

export default PhotoVerificationPage;
